// Matches keys that look like [[#key]], [[!key]], or [[/key]], where `key` cannot include `[` or `]`.
// This is not global so that it does not get mutated when running `exec`.
const SPLIT_MARKER = /\[\[([!#/][^[\]]+)]]/;

/**
 * Types used for splitting:
 *
 * @typdef {Object} SplitToken
 * @property {String} type - Can be 'TEMPLATE', '#', '/', or '!'.
 * @property {String} value - For the 'TEMPLATE' type, it will give the substring of the template in this part of the split.
 *                            For the other types, it gives the name of the key used in the split.
 *
 * @typedef {(TemplateNode|TextNode|KeyNode)} SplitNode
 *
 * @typedef {Object} TemplateNode
 * @property {String} template - The template string that will be used with the binding.
 *
 * @typedef {Object} TextNode
 * @property {String} text - The text from the processed template string.
 *
 * @typedef {Object} KeyNode
 * @property {String} key - The key used to create the node (through either [[#key]][[/key]] or [[!key]])
 * @property {Array.<(SplitNode)>} content - The nodes contained by this node. This will be empty for nodes created through [[!key]].
 *
 * @callback InvalidCallback
 * @param {String} reason - The reason that the split was invalid
 * @returns {*} - The value will be propagated up to the user
 */

/**
 * Splits a template into tokens based on split keys. It leaves non-split values alone.
 *
 * @example
 *
 * splitTokenize('Click [[#link]]here[[/link]] for more {info} about [[!content]]!');
 * [
 *   { type: 'TEMPLATE', value: 'Click ' },
 *   { type: '#', value: 'link' },
 *   { type: 'TEMPLATE', value: 'here' },
 *   { type: '/', value: 'link' },
 *   { type: 'TEMPLATE', value: ' for more {info} about '},
 *   { type: '!', value: 'content' },
 *   { type: 'TEMPLATE', value: '!' }
 * ]
 *
 * @param {String} template
 * @returns {Array.<SplitToken>}
 */
export function splitTokenize(template) {
  const tokens = [];
  let templateRemaining = template;
  let foundResult;
  while ((foundResult = SPLIT_MARKER.exec(templateRemaining)) !== null) {
    tokens.push({
      type: 'TEMPLATE',
      value: templateRemaining.slice(0, Math.max(0, foundResult.index))
    }, {
      type: foundResult[1].charAt(0),
      value: foundResult[1].slice(1)
    });
    templateRemaining = templateRemaining.slice(foundResult.index + foundResult[0].length);
  }
  tokens.push({
    type: 'TEMPLATE',
    value: templateRemaining
  });
  return tokens;
}

/**
 * Converts an array of tokens into a nested structure representing the split.
 * @example
 *
 * var splitResult = [];
 * splitNested([
 *   { type: 'TEMPLATE', value: 'Click ' },
 *   { type: '#', value: 'link' },
 *   { type: 'TEMPLATE', value: 'here' },
 *   { type: '/', value: 'link' },
 *   { type: 'TEMPLATE', value: ' for more {info} about '},
 *   { type: '!', value: 'content' },
 *   { type: 'TEMPLATE', value: '!' }
 * ], 0, splitResult, null, invalidCallback);
 * { index: 7 }
 *
 * splitResult;
 * [
 *   { template: 'Click ' },
 *   { key: 'link', content: [{ template: 'here' }]},
 *   { template: ' for more {info} about ' },
 *   { key: 'content', content: [] },
 *   { template: '!' }
 * ]
 *
 * @param {SplitToken[]} tokens - Should be the result of using splitTokenize on the initial template.
 * @param {Number} index - The index of the next token to process.
 * @param {SplitNode[]} splitResult - This should start out as an empty array that will be mutated to contain the result of parsing the tokens.
 * @param {String} openKey - The key of the most recent `#` token that hasn't met a corresponding `/` token. Should be `null` to represent no open keys.
 * @param {InvalidCallback} invalidCallback - function to be called with the reason for an error.
 *
 * @returns {Object} If an error was encountered, it will have the key 'invalid' with the value being the result of calling `invalidCallback` with the reason.
 *                   Otherwise, it will have the key 'index' with the value of the last index checked.
 */

export function splitNested(tokens, index, splitResult, openKey, invalidCallback) {
  if (index >= tokens.length) {
    if (openKey !== null) {
      return {
        invalid: invalidCallback(`reached end of string when \`/${openKey}\` was expected.`)
      };
    } else {
      return {
        index
      };
    }
  }
  const token = tokens[index];
  let nextIndex = index + 1;
  const nestedSplitResult = [];
  let result;
  switch (token.type) {
    case 'TEMPLATE':
      splitResult.push({
        text: token.value
      });
      break;
    case '!':
      splitResult.push({
        key: token.value,
        content: []
      });
      break;
    case '#':
      splitResult.push({
        key: token.value,
        content: nestedSplitResult
      });
      result = splitNested(tokens, nextIndex, nestedSplitResult, token.value, invalidCallback);
      if (result.invalid) {
        return result;
      } else {
        nextIndex = result.index;
      }
      break;
    case '/':
      if (token.value !== openKey) {
        if (openKey === null) {
          return {
            invalid: invalidCallback(`found \`/${token.value}\` when no closing key was expected.`)
          };
        } else {
          return {
            invalid: invalidCallback(`found \`/${token.value}\` when \`/${openKey}\` was expected.`)
          };
        }
      } else {
        return {
          index: nextIndex
        };
      }
    default:
      break;
  }
  return splitNested(tokens, nextIndex, splitResult, openKey, invalidCallback);
}

/**
 * Converts a template into a nested structure representing the split.
 *
 * @param {String} template
 * @param {InvalidCallback} invalidCallback
 *
 * @returns {(Object|SplitNode[])} If the result was invalid, it will return an object with the key 'invalid' mapped to the result of calling `invalidCallback` with the reason.
 *                                 Otherwise, it returns an array of SplitNodes that represents the split.
 */
export function splitNode(template, invalidCallback) {
  const tokens = splitTokenize(template);
  const splitResult = [];
  const result = splitNested(tokens, 0, splitResult, null, invalidCallback);
  if (result.invalid) {
    return result;
  } else {
    return splitResult;
  }
}